"use client";

import { useEffect, useRef, useState } from "react";
import { useAppData } from "@/store/appStore";
import { syncUserProperties } from "@/lib/utils/gtm-events";
import { usePathname } from "next/navigation";

export const UserPropertiesSync = () => {
	const [appData] = useAppData();
	const pathname = usePathname();
	const [clevertapReady, setClevertapReady] = useState(false);

	// Track previous values to detect real changes
	const prevRef = useRef({
		userId: null as string | null,
		ctc: null as number | null,
		pathname: "" as string,
		hasSynced: false,
		clevertapSynced: false,
	});

	// Listen for CleverTap SDK ready
	useEffect(() => {
		const handler = () => setClevertapReady(true);
		window.addEventListener("clevertap_ready", handler);
		if (
			window.clevertap &&
			typeof window.clevertap.event?.push === "function" &&
			!Array.isArray(window.clevertap.event)
		) {
			setClevertapReady(true);
		}
		return () => window.removeEventListener("clevertap_ready", handler);
	}, []);

	useEffect(() => {
		// Skip if appData isn't loaded yet
		if (!appData) return;

		const currentUserId = appData.user?.id || null;
		const currentCTC = appData.ctc || null;
		const isAuthorized = !!currentUserId;

		// Detect what changed
		const prevUserId = prevRef.current.userId;
		const userLoggedIn = isAuthorized && prevUserId !== currentUserId; // catches null→id AND userA→userB
		const ctcChanged = currentCTC !== prevRef.current.ctc;
		const pathChanged = pathname !== prevRef.current.pathname;
		const isFirstSync = !prevRef.current.hasSynced;

		// Always track userId so logout (id→null) is recorded even if no sync fires
		prevRef.current.userId = currentUserId;

		// Sync conditions:
		// 1. First time sync (with or without CTC)
		// 2. User just logged in (guest → authorized, or user switch)
		// 3. CTC changed
		// 4. Path changed (for UTM updates on authorized users)
		const clevertapJustLoaded =
			clevertapReady &&
			!prevRef.current.clevertapSynced &&
			prevRef.current.hasSynced;

		const shouldSync =
			isFirstSync ||
			userLoggedIn ||
			clevertapJustLoaded ||
			(ctcChanged && prevRef.current.hasSynced) ||
			(pathChanged && isAuthorized);

		if (shouldSync) {
			syncUserProperties(appData, userLoggedIn);

			// Update tracking refs
			prevRef.current = {
				userId: currentUserId,
				ctc: currentCTC,
				pathname: pathname,
				hasSynced: true,
				clevertapSynced: clevertapReady,
			};
		}
	}, [
		appData, // Catch all appData changes
		appData?.user?.id, // Explicit: user login/logout
		appData?.ctc, // Explicit: CTC updates
		pathname, // Explicit: navigation
		clevertapReady, // Re-sync when CleverTap SDK loads
	]);

	return null;
};
